<template>
  <div class="archive-container">
    <h1>Archive</h1>
    <div class="search-sort-container">
      <SearchEntries :entries="filteredEntries" @filtered="updateFilteredEntries" />
      <select v-model="sortOrder" @change="sortEntries">
        <option value="newest">Newest First</option>
        <option value="oldest">Oldest First</option>
        <option value="favorite">Favorite</option>
      </select>
    </div>
    <ul>
      <li v-for="entry in filteredEntries" :key="entry.id">
        <h3>{{ entry.title }}</h3>
        <p>{{ entry.content }}</p>
        <small>{{ formatDate(entry.date) }}</small>
        <button @click="deleteEntry(entry.id)">Delete</button>
        <button @click="openEditModal(entry)">Edit</button>
        <button @click="toggleFavorite(entry)">{{ entry.favorite ? 'Unfavorite' : 'Favorite' }}</button>
      </li>
    </ul>
    <!-- Edit Modal -->
    <div v-if="editModalOpen" class="modal">
      <form @submit.prevent="updateEntry">
        <input v-model="editEntry.title" placeholder="Title" />
        <textarea v-model="editEntry.content" placeholder="Content"></textarea>
        <button type="submit">Save</button>
        <button @click="closeEditModal">Cancel</button>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { db } from '../firebase-config';
import { collection, doc, deleteDoc, updateDoc, getDocs } from 'firebase/firestore';
import SearchEntries from './SearchEntries.vue';

export default {
  components: {
    SearchEntries
  },
  setup() {
    const entries = ref([]);
    const editEntry = ref({});
    const editModalOpen = ref(false);
    const searchQuery = ref('');
    const sortOrder = ref('newest');

    const fetchEntries = async () => {
      const querySnapshot = await getDocs(collection(db, "journalEntries"));
      entries.value = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), date: doc.data().date.toDate() }));
    };

    onMounted(fetchEntries);

    const filteredEntries = computed(() => {
      let result = entries.value.filter(entry => entry.title.toLowerCase().includes(searchQuery.value.toLowerCase()));
      if (sortOrder.value === 'newest') {
        result.sort((a, b) => b.date - a.date);
      } else if (sortOrder.value === 'oldest') {
        result.sort((a, b) => a.date - b.date);
      } else if (sortOrder.value === 'favorite') {
        result.sort((a, b) => (b.favorite ? 1 : -1) - (a.favorite ? 1 : -1));
      }
      return result;
    });

    const deleteEntry = async (id) => {
      await deleteDoc(doc(db, "journalEntries", id));
      fetchEntries();
    };

    const openEditModal = (entry) => {
      editEntry.value = { ...entry };
      editModalOpen.value = true;
    };

    const updateEntry = async () => {
      const entryRef = doc(db, "journalEntries", editEntry.value.id);
      await updateDoc(entryRef, {
        title: editEntry.value.title,
        content: editEntry.value.content
      });
      editModalOpen.value = false;
      fetchEntries();
    };

    const closeEditModal = () => {
      editModalOpen.value = false;
      fetchEntries();
    };

    const toggleFavorite = async (entry) => {
      const entryRef = doc(db, "journalEntries", entry.id);
      await updateDoc(entryRef, {
        favorite: !entry.favorite
      });
      fetchEntries();
    };

    const updateFilteredEntries = (filtered) => {
      entries.value = filtered;
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    };

    return {
      filteredEntries,
      deleteEntry,
      openEditModal,
      editModalOpen,
      editEntry,
      updateEntry,
      closeEditModal,
      searchQuery,
      sortOrder,
      toggleFavorite,
      updateFilteredEntries,
      formatDate
    };
  }
};
</script>

<style scoped>
.archive-container {
  max-width: 800px;
  margin: auto;
  padding: 20px;
}

h1 {
  color: #333;
  text-align: center;
}

.search-sort-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

select {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  background: #f9f9f9;
  border: 1px solid #ddd;
  margin-top: 10px;
  padding: 10px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

li:hover {
  background-color: #f0f0f0;
}

h3 {
  margin: 0;
  color: #5C67F2;
}

p {
  color: #666;
  line-height: 1.5;
}

small {
  display: block;
  margin-top: 10px;
  color: #999;
}

button {
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  margin-right: 10px;
  cursor: pointer;
  background-color: #5C67F2;
  color: white;
}

button:hover {
  background-color: #4a54f1;
}

.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 30px; /* Increased padding for more space */
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  width: 90%; /* Modal takes up more width */
  max-width: 600px; /* Increased maximum width */
  font-size: 16px; /* Larger text for better readability */
}
input, textarea {
  width: 100%; /* Full width for inputs */
  padding: 10px; /* More padding for inputs */
  margin-bottom: 10px; /* Space between inputs */
}
</style>

